import { useEffect, useMemo, useRef, useState } from 'react'

import { collectLiveAssetReferences } from '../helpers/collectAssetLinkIds'
import { isContentfulPreviewFrame } from '../helpers/isContentfulPreviewFrame'
import {
  ContentfulPreviewAsset,
  ContentfulPreviewEntry,
  fetchContentfulPreviewAsset,
} from '../services/contentfulPreviewService'
import { isContentfulAssetFile, isRecord } from '../helpers/overlayContentfulFields/shared'

const NO_ASSETS: ContentfulPreviewAsset[] = []
const NO_IDS: string[] = []

const assetFileUrl = (asset: ContentfulPreviewAsset): string | undefined => {
  const file = isRecord(asset.fields) ? asset.fields.file : undefined
  return isContentfulAssetFile(file) ? file.url : undefined
}

/**
 * Keeps every asset the live entries reference available to the overlay, with a current file.
 *
 * The page include graph is fetched once, so the overlay needs help in two cases, and missing
 * either one means an image swap silently does nothing:
 *
 * 1. **A newly linked asset** is one we have never seen, so it has to be fetched by id.
 * 2. **A re-uploaded file** keeps the asset id and changes only the file URL, so the asset we
 *    already hold is stale. Fetching only unknown ids would skip exactly this case.
 *
 * Only the develop proxy can build `gatsbyImageData`, so the inline file live preview sends is used
 * to *detect* staleness, not to render — the refetch is what produces the image.
 *
 * Each `id@url` pair is attempted once. A draft asset with nothing uploaded, or a failed request,
 * must not turn into a fetch loop on every keystroke; the field simply stays on build-time data
 * until something about the reference actually changes.
 */
export const useContentfulPreviewAssets = (
  payloadAssets: ContentfulPreviewAsset[],
  liveEntries: ContentfulPreviewEntry[],
): ContentfulPreviewAsset[] => {
  const [fetchedAssets, setFetchedAssets] = useState<ContentfulPreviewAsset[]>(NO_ASSETS)
  const attempted = useRef(new Set<string>())

  // Later entries win, so a refetched asset replaces the stale one from the page payload.
  const assets = useMemo(() => {
    if (!fetchedAssets.length) return payloadAssets

    const byId = new Map<string, ContentfulPreviewAsset>()
    payloadAssets.forEach(asset => byId.set(asset.sys.id, asset))
    fetchedAssets.forEach(asset => byId.set(asset.sys.id, asset))

    return Array.from(byId.values())
  }, [payloadAssets, fetchedAssets])

  const staleIds = useMemo(() => {
    if (!isContentfulPreviewFrame()) return NO_IDS

    const known = new Map(assets.map(asset => [asset.sys.id, assetFileUrl(asset)]))

    return collectLiveAssetReferences(liveEntries)
      .filter(reference => {
        if (!known.has(reference.id)) return true
        // Same asset, different file: what we hold no longer describes what the editor sees.
        return Boolean(reference.url) && known.get(reference.id) !== reference.url
      })
      .map(reference => `${reference.id}@${reference.url || ''}`)
      .filter(key => !attempted.current.has(key))
  }, [assets, liveEntries])

  useEffect(() => {
    if (!staleIds.length) return

    let isCurrent = true
    staleIds.forEach(key => attempted.current.add(key))

    Promise.all(
      staleIds.map(key =>
        fetchContentfulPreviewAsset(key.split('@')[0]).catch(error => {
          console.error(`Falling back to build time image for asset ${key.split('@')[0]}.`, error)
          return undefined
        }),
      ),
    ).then(results => {
      const resolved = results.filter((asset): asset is ContentfulPreviewAsset => Boolean(asset && asset.sys))
      if (isCurrent && resolved.length) setFetchedAssets(current => [...current, ...resolved])
    })

    return () => {
      isCurrent = false
    }
  }, [staleIds])

  return assets
}
